// Port of `ScriptFormat` and `ProjectKind` (SuiteModel/ScriptFormat.swift; `design-docs/Stage plays — design.md`,
// `Musicals, radio and sitcoms — design.md`): how a script is laid out — a film script, a multi-camera sitcom, a stage
// play in US or UK manuscript format (a musical is a play with `settings.musical`), or a radio play in the BBC's scene
// style — kept in `settings.scriptFormat` (absent = screen). A play is a screenplay project with a stage format, so a
// build from before plays shows it as a screenplay. A format this build doesn't know is kept as stored and laid out as a
// screenplay.
import type { ProjectType } from './contract.ts';

/** `ScriptFormat.rawValue`: 'screen', 'multicam', 'stage-us', 'stage-uk', 'radio', or a later build's raw value. */
export type ScriptFormat = 'screen' | 'multicam' | 'stage-us' | 'stage-uk' | 'radio' | (string & {});

/** `ScriptFormat.choices`, in menu order. */
export const SCRIPT_FORMATS: readonly ScriptFormat[] = ['screen', 'multicam', 'stage-us', 'stage-uk', 'radio'];

export const isStageFormat = (f: ScriptFormat): boolean => f === 'stage-us' || f === 'stage-uk';

/** `ScriptFormat.Family`: a script is offered its own family's presets only. */
export type ScriptFamily = 'screen' | 'tv' | 'stage' | 'audio';

/** `ScriptFormat.family` (an unknown format is film's). */
export function scriptFamily(f: ScriptFormat): ScriptFamily {
  switch (f) {
    case 'multicam': return 'tv';
    case 'stage-us': case 'stage-uk': return 'stage';
    case 'radio': return 'audio';
    default: return 'screen';
  }
}
export const isUnsupportedFormat = (f: ScriptFormat): boolean => !(SCRIPT_FORMATS as readonly string[]).includes(f);

/** `ScriptFormat.name`. */
export function scriptFormatName(f: ScriptFormat): string {
  switch (f) {
    case 'screen': return 'Screenplay';
    case 'multicam': return 'Sitcom (multi-camera)';
    case 'stage-us': return 'Stage play (US)';
    case 'stage-uk': return 'Stage play (UK)';
    case 'radio': return 'Radio play (BBC)';
    default: return f;
  }
}

/** `ScriptFormat.stage(forRegion:)`: UK manuscript format in Britain and Ireland, US everywhere else. */
export const stageFormatForRegion = (region: string | null | undefined): ScriptFormat =>
  ['GB', 'IE'].includes((region ?? '').toUpperCase()) ? 'stage-uk' : 'stage-us';

/** `ProjectKind`: the project as the writer sees it — a screenplay in a stage format is a play, in the radio format a
 *  radio play. */
export type ProjectKind = 'manuscript' | 'screenplay' | 'stage-play' | 'radio-play' | 'journal';

export const PROJECT_KINDS: readonly ProjectKind[] = ['manuscript', 'screenplay', 'stage-play', 'radio-play', 'journal'];

/** `ProjectKind(type:scriptFormat:)`. */
export function projectKind(type: ProjectType, format: ScriptFormat): ProjectKind {
  if (type === 'screenplay') return isStageFormat(format) ? 'stage-play' : format === 'radio' ? 'radio-play' : 'screenplay';
  return type;
}

/** `ProjectKind.isScript`: its text is Fountain. */
export const isScriptKind = (k: ProjectKind): boolean => k === 'screenplay' || k === 'stage-play' || k === 'radio-play';

/** `ProjectKind.displayName(_:format:musical:)`: a musical "Musical", a sitcom "Sitcom", else the kind's name. */
export function projectDisplayName(kind: ProjectKind, format: ScriptFormat, musical: boolean): string {
  if (kind === 'stage-play' && musical) return 'Musical';
  if (kind === 'screenplay' && format === 'multicam') return 'Sitcom';
  return projectKindName(kind);
}

/** `ProjectKind.name`. */
export function projectKindName(kind: ProjectKind): string {
  switch (kind) {
    case 'manuscript': return 'Manuscript';
    case 'screenplay': return 'Screenplay';
    case 'stage-play': return 'Stage play';
    case 'radio-play': return 'Radio play';
    case 'journal': return 'Journal';
  }
}

/** `ProjectDocument.scriptFormat` from the settings map: 'screen' when absent, and for books and journals. */
export function scriptFormatOf(type: ProjectType, settings: Record<string, unknown>): ScriptFormat {
  const raw = settings.scriptFormat;
  return type === 'screenplay' && typeof raw === 'string' ? raw : 'screen';
}

/** `ProjectDocument.isMusical`: a play with `settings.musical` (stored only while true). */
export function isMusicalOf(type: ProjectType, settings: Record<string, unknown>): boolean {
  return isStageFormat(scriptFormatOf(type, settings)) && settings.musical === true;
}

/** `ProjectTemplate.poetryID`: the poetry collection template's id, recorded in `settings.template`. */
export const POETRY_TEMPLATE_ID = 'poetry';

/** `ProjectDocument.isPoetry`: a poetry collection, whose documents are poems and whose formats set them as verse. */
export function isPoetryOf(settings: Record<string, unknown>): boolean {
  return settings.template === POETRY_TEMPLATE_ID;
}

/** `ProjectDocument.manuscriptName`: the name a script's manuscript is made with. */
export function scriptManuscriptName(format: ScriptFormat, musical: boolean): string {
  if (isStageFormat(format)) return musical ? 'Musical' : 'Play';
  if (format === 'multicam') return 'Episode';
  if (format === 'radio') return 'Radio play';
  return 'Screenplay';
}

/** `ProjectDocument.madeManuscriptNames`: a manuscript still called one of these follows the format. */
export const MADE_MANUSCRIPT_NAMES: ReadonlySet<string> = new Set(['Screenplay', 'Play', 'Musical', 'Episode', 'Radio play']);
